import React from "react"
import { useTranslation } from 'react-i18next'
import { Offcanvas } from 'react-bootstrap'
import { format } from 'date-fns'

import {
	PAYMENT_ROWS,
	CASH_ROWS,
	CURRENCY_SIGNS,
	calcDifference,
	calcToSafe,
	differenceState,
	formatAmountInput,
	formatSum,
	hasErrors,
	isEmptyAmount,
	sanitizeAmountInput,
} from './shiftCloseCalc'

function formatOpenDate(value) {
	return value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : '—'
}

// shift close side panel: step 1 recount, step 2 cash left in the cashbox / safe
function ShiftClosePanel({ show, step, shift, expected, fact, leave, comment, factErrors, leaveErrors,
	onFactChange, onFillExpected, onLeaveChange, onLeaveFill, onCommentChange, onNext, onBack, onCancel, onFinish }) {
	const { t } = useTranslation()

	const factIncomplete = PAYMENT_ROWS.some(row => isEmptyAmount(fact[row.key]))
	const leaveTouched = CASH_ROWS.some(row => !isEmptyAmount(leave[row.key]))

	function renderRowName(row) {
		return (
			<div className="sc-row-name">
				<i className={`uil ${row.cash ? 'uil-money-bill' : 'uil-credit-card'}`}></i>
				{t(row.i18n)}
			</div>
		)
	}

	function renderAmountInput(value, currency, hasError, onChange) {
		return (
			<div className={`sc-input-wrap ${hasError ? 'sc-input-error' : ''}`}>
				<input
					type="text"
					inputMode="decimal"
					className="form-control"
					placeholder="0"
					value={formatAmountInput(value)}
					onChange={(e) => onChange(sanitizeAmountInput(e.target.value))}
				/>
				<span className="sc-input-suffix">{CURRENCY_SIGNS[currency]}</span>
			</div>
		)
	}

	function renderDifference(row) {
		if (isEmptyAmount(fact[row.key])) return <span className="sc-muted-dash">—</span>
		const difference = calcDifference(fact[row.key], expected[row.key], row.currency)
		const state = differenceState(difference)
		if (state === 'zero') return <span className="sc-diff-zero"><i className="uil uil-check"></i></span>
		return <span className={`sc-diff-${state}`}>{difference > 0 && '+'}{formatSum(difference, row.currency)}</span>
	}

	function renderToSafe(row) {
		if (!leaveTouched || leaveErrors[row.key]) return <span className="sc-muted-dash">—</span>
		return <b>{formatSum(calcToSafe(fact[row.key], leave[row.key], row.currency), row.currency)}</b>
	}

	return (
		<Offcanvas show={show} onHide={onCancel} placement="end" backdrop="static" className="sc-panel">
			<Offcanvas.Header closeButton>
				<Offcanvas.Title>{t('SC_CHECK_TITLE')}</Offcanvas.Title>
			</Offcanvas.Header>

			<Offcanvas.Body>
				<div className="sc-info">
					<span>{t('TRADING_POINT')}</span><b>{shift?.posName || '—'}</b>
					<span>{t('CASHBOX')}</span><b>{shift?.cashName || '—'}</b>
					<span>{t('CASHIER')}</span><b>{shift?.cashierName || '—'}</b>
					<span>{t('SHIFT_OPENED')}</span><b>{formatOpenDate(shift?.statusDate)}</b>
				</div>

				<div className="sc-steps">
					<div className={`sc-step ${step === 1 ? 'active' : 'done'}`}>
						{step > 1 && <i className="uil uil-check"></i>}
						1. {t('SC_STEP_RECOUNT')}
					</div>
					<div className={`sc-step ${step === 2 ? 'active' : ''}`}>
						2. {t('SC_STEP_SAFE')}
					</div>
				</div>

				{step === 1 ?
					<>
						<div className="sc-grid">
							<div className="sc-grid-head">
								<div>{t('SC_PAYMENT_TYPE')}</div>
								<div className="text-end">{t('SC_EXPECTED')}</div>
								<div className="sc-head-with-btn">
									<button type="button" className="sc-icon-btn" title={t('SC_FILL_EXPECTED')} onClick={onFillExpected}>
										<i className="uil uil-arrow-to-right"></i>
									</button>
									{t('SC_FACT')}
								</div>
								<div className="text-end">{t('SC_DIFFERENCE')}</div>
							</div>

							{PAYMENT_ROWS.map(row => (
								<div className="sc-grid-row" key={row.key}>
									{renderRowName(row)}
									<div className="sc-amount">{formatSum(expected[row.key], row.currency)}</div>
									{renderAmountInput(fact[row.key], row.currency, factErrors[row.key], (value) => onFactChange(row.key, value))}
									<div className="text-end">{renderDifference(row)}</div>
								</div>
							))}
						</div>

						{hasErrors(factErrors) &&
							<p className="sc-error-text">{t('SC_ERROR_FACT_REQUIRED')}</p>
						}

						<div className="sc-comment">
							<label>{t('COMMENT')}</label>
							<textarea
								rows={2}
								className="form-control"
								placeholder={t('SC_COMMENT_PLACEHOLDER')}
								value={comment}
								onChange={(e) => onCommentChange(e.target.value)}
							/>
						</div>
					</>
					:
					<div className="sc-grid">
						<div className="sc-grid-head">
							<div>{t('SC_PAYMENT_TYPE')}</div>
							<div className="text-end">{t('SC_FACT_BALANCE')}</div>
							<div className="sc-head-with-btn">
								<button type="button" className="sc-icon-btn" title={t('SC_FILL_LEAVE_ALL')} onClick={() => onLeaveFill('cashbox')}>
									<i className="uil uil-lock-alt"></i>
								</button>
								{t('SC_LEAVE_IN_CASHBOX')}
							</div>
							<div className="sc-head-with-btn justify-content-end">
								<button type="button" className="sc-icon-btn" title={t('SC_FILL_ALL_TO_SAFE')} onClick={() => onLeaveFill('safe')}>
									<i className="uil uil-university"></i>
								</button>
								{t('SC_TO_SAFE')}
							</div>
						</div>

						{PAYMENT_ROWS.map(row => (
							<div className="sc-grid-row" key={row.key}>
								{renderRowName(row)}
								<div className="sc-amount">{formatSum(fact[row.key], row.currency)}</div>
								{row.cash ?
									<div>
										{renderAmountInput(leave[row.key], row.currency, leaveErrors[row.key], (value) => onLeaveChange(row.key, value))}
										{leaveErrors[row.key] && <small className="sc-error-text">{t(leaveErrors[row.key])}</small>}
									</div>
									:
									<div className="sc-all-to-safe">{t('SC_ALL_TO_SAFE')}</div>
								}
								<div className="text-end">
									{row.cash ? renderToSafe(row) : <b>{formatSum(fact[row.key], row.currency)}</b>}
								</div>
							</div>
						))}
					</div>
				}
			</Offcanvas.Body>

			<div className="sc-footer">
				{step === 2 &&
					<button type="button" className="btn sc-btn-back" onClick={onBack}>
						<i className="uil uil-arrow-left"></i>
						{t('BACK')}
					</button>
				}
				<div className="sc-footer-right">
					<button type="button" className="btn sc-btn-cancel" onClick={onCancel}>{t('CANCEL')}</button>
					{step === 1 ?
						<button type="button" className="btn sc-btn-primary" disabled={factIncomplete} onClick={onNext}>
							<i className="uil uil-arrow-right"></i>
							{t('SC_NEXT')}
						</button>
						:
						<button type="button" className="btn sc-btn-primary" disabled={hasErrors(leaveErrors)} onClick={onFinish}>
							<i className="uil uil-check"></i>
							{t('SC_FINISH')}
						</button>
					}
				</div>
			</div>
		</Offcanvas>
	)
}

export default ShiftClosePanel
